import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Anchor, Lock, Mail, Loader2, ShieldCheck, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.first_name}`);
      if (u.must_change_password) {
        navigate("/force-password-change", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex crew-login-hero crew-grain relative">
      {/* left panel — brand */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-14 text-white relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center">
            <Anchor className="w-6 h-6 text-cyan-300" />
          </div>
          <div>
            <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>CrewOn</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80">Crew Manning ERP</div>
          </div>
        </div>

        <div className="max-w-lg space-y-6">
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight" style={{ fontFamily: "Manrope" }}>
            Command every voyage,
            <br />
            <span className="text-cyan-300">from shore to seafarer.</span>
          </h1>
          <p className="text-slate-300 text-base max-w-md">
            A single source of truth for your manning operations — crew sourcing,
            documents, SEA contracts and audit-grade compliance.
          </p>

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="rounded-lg border border-white/10 bg-white/5 backdrop-blur p-4">
              <ShieldCheck className="w-5 h-5 text-cyan-300" />
              <div className="mt-2 text-sm font-semibold">Audit-grade logging</div>
              <div className="text-xs text-slate-400 mt-1">Every login tracked with IP & device.</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 backdrop-blur p-4">
              <LifeBuoy className="w-5 h-5 text-cyan-300" />
              <div className="mt-2 text-sm font-semibold">Role-based control</div>
              <div className="text-xs text-slate-400 mt-1">Unlimited custom roles. No developer needed.</div>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-400">© {new Date().getFullYear()} CrewOn Manning Agency</div>
      </div>

      {/* right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-14 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <Anchor className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>CrewOn</div>
            </div>

            <h2 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
              Sign in
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Access your crew manning workspace.
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-9 h-11"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-9 h-11"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-cyan-700 hover:text-cyan-800 font-medium" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 text-sm font-semibold bg-[#0b192c] hover:bg-[#0f2544] transition-colors"
                data-testid="login-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500 text-center">
              Authorized personnel only. All login attempts are recorded.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
